/* ============================================================
   CART LINES & ORDER MESSAGE
   Extracted from StoreContext so this logic can be exercised
   directly. Getting the WhatsApp message wrong is expensive —
   it is what the shop actually fulfils the order from.
   ============================================================ */

/**
 * A cart line is identified by product **and** colour.
 *
 * This is the whole reason the same ring in Gold and in Rose Gold stay
 * as two separate lines with their own quantities, instead of silently
 * merging into one line and shipping the wrong colour.
 */
export const lineKeyFor = (id, colorName) => `${id}::${colorName || ''}`;

/** Subcategory of a product or cart line, tolerating the older field name. */
export const subcatOf = (p) => (p && (p.subCat || p.necklaceCat)) || '';

/**
 * Build a new cart line from a product plus the shopper's choices.
 *
 * @param {object} product
 * @param {{color?:{name:string,hex:string}, size?:string}} [choice]
 */
export function buildCartLine(product, choice = {}) {
  const color = choice.color || null;
  const size = choice.size || product.dimensions || '';

  // The exact photo the shopper was viewing wins; then the photo that
  // actually shows the chosen colour; then the product's general photos.
  const thumb =
    choice.img ||
    (color?.name && product.colorImages?.[color.name]?.[0]) ||
    product.images?.[0] ||
    product.img ||
    '';

  return {
    ...product,
    key: lineKeyFor(product.id, color?.name),
    qty: 1,
    color,
    colorName: color?.name || '',
    colorHex: color?.hex || '',
    size,
    img: thumb,
  };
}

/**
 * Add a line to the cart, merging with an existing line only when the
 * product *and* the colour match.
 */
export function addLine(cart, product, choice = {}) {
  const key = lineKeyFor(product.id, choice.color?.name);
  const existing = cart.find((x) => x.key === key);
  if (existing) {
    return cart.map((x) => (x.key === key ? { ...x, qty: x.qty + 1 } : x));
  }
  return [...cart, buildCartLine(product, choice)];
}

/** Match a line by its key, or by bare product id for older call sites. */
export const matchesLine = (line, ref) => line.key === ref || line.id === ref;

export function changeLineQty(cart, ref, delta) {
  return cart
    .map((x) => (matchesLine(x, ref) ? { ...x, qty: x.qty + delta } : x))
    .filter((x) => x.qty > 0);
}

export function removeLine(cart, ref) {
  return cart.filter((x) => !matchesLine(x, ref));
}

export const cartCountOf = (cart) => cart.reduce((s, i) => s + i.qty, 0);
export const cartTotalOf = (cart) => cart.reduce((s, i) => s + i.price * i.qty, 0);

/**
 * One numbered block of the WhatsApp order message.
 *
 * @param {object} item  cart line
 * @param {number} idx   zero-based position
 * @param {Function} money  currency formatter
 */
export function orderLineText(item, idx, money) {
  const sub = subcatOf(item);
  const rows = [
    `${idx + 1}. ${item.name}`,
    `   Category: ${item.cat || 'Jewelry'}${sub ? ' › ' + sub : ''}`,
  ];
  if (item.colorName) {
    rows.push(`   Color: ${item.colorName}${item.colorHex ? ` (${item.colorHex})` : ''}`);
  }
  if (item.size) rows.push(`   Size: ${item.size}`);
  rows.push(`   Quantity: ${item.qty}`);
  rows.push(`   Price: ${money(item.price * item.qty)}`);
  /* Photo link so the shop fulfils the exact design the shopper saw. */
  if (item.img) rows.push(`   Photo: ${item.img}`);
  return rows.join('\n');
}

/**
 * The full message sent to WhatsApp at checkout.
 *
 * @param {Array} cart
 * @param {{name:string, email:string}} customer
 * @param {Function} money
 */
export function buildOrderMessage(cart, customer, money) {
  const lines = cart.map((item, i) => orderLineText(item, i, money)).join('\n\n');
  const total = cartTotalOf(cart);
  return `Assalam o Alaikum!\n\nName: ${customer.name}\nEmail: ${customer.email}\n\n🛍️ Meri Order:\n\n${lines}\n\n━━━━━━━━\nTotal: ${money(total)}\n━━━━━━━━\n\nPlease confirm. Shukriya!`;
}

/** The item shape written to the `orders` document in Firestore. */
export function orderItemsPayload(cart) {
  return cart.map((i) => ({
    id: i.id,
    name: i.name,
    cat: i.cat || '',
    subCat: subcatOf(i),
    qty: i.qty,
    price: i.price,
    img: i.img || '',
    color: i.colorName || '',
    colorHex: i.colorHex || '',
    size: i.size || '',
  }));
}
